// Keeps the Vuex API (`store.getters['module/name']`, `commit`, `dispatch`, `map*`, `$store`)
// over the Pinia stores: callers keep their code, only their import moved from `vuex` (KP-7419).
import { getActivePinia } from 'pinia';
import * as stores from '~/store/stores';

/**
 * Pinia store of a Vuex module, e.g. `navigation` → `useNavigationStore`. Read at call time: the
 * modules import this file back (through Apollo and customFetch) while `stores` is evaluated.
 * @param {string} moduleName
 * @param {Object} [pinia]
 * @returns {Object|undefined} undefined for an unknown module, as Vuex
 */
const moduleStore = (moduleName, pinia) => (typeof moduleName === 'string' && moduleName
  ? stores[`use${moduleName[0].toUpperCase()}${moduleName.slice(1)}Store`]?.(pinia)
  : undefined);

/**
 * The Vuex store API over the Pinia stores of an instance
 * @param {Object} [pinia] - Pinia instance; the active one at each call when omitted
 * @returns {Object}
 */
const createVuexStore = (pinia) => {
  // Store and name targeted by a Vuex type such as `navigation/machineSelected`
  const resolve = (type) => {
    // A non-string key (a Symbol read by `String()` or a test printer) targets nothing
    const [moduleName, name] = typeof type === 'string' ? type.split('/') : [];
    return [moduleStore(moduleName, pinia), name];
  };

  return {
    state: new Proxy({}, { get: (_, moduleName) => moduleStore(moduleName, pinia)?.$state }),
    getters: new Proxy({}, {
      get: (_, type) => {
        const [target, name] = resolve(type);
        // See defineStoreFromModule: a getter named after a state key is exposed as `<name>Getter`
        return target && (name in target.$state ? target[`${name}Getter`] : target[name]);
      },
    }),
    commit(type, payload) {
      const [target, name] = resolve(type);
      if (typeof target?.[name] !== 'function') {
        // eslint-disable-next-line no-console
        console.error(`[vuex] unknown mutation type: ${type}`);
        return;
      }
      target[name](payload);
    },
    dispatch(type, payload) {
      const [target, name] = resolve(type);
      if (typeof target?.[name] !== 'function') {
        // eslint-disable-next-line no-console
        console.error(`[vuex] unknown action type: ${type}`);
        return undefined;
      }
      return Promise.resolve(target[name](payload));
    },
    install(app) {
      // eslint-disable-next-line no-param-reassign
      app.config.globalProperties.$store = this;
    },
  };
};

// One Vuex store per Pinia instance, shared by `useStore()` and `$store` as Vuex did
const vuexStores = new WeakMap();
const vuexStoreOf = (pinia) => {
  if (!vuexStores.has(pinia)) {
    vuexStores.set(pinia, createVuexStore(pinia));
  }
  return vuexStores.get(pinia);
};

// For the code outside components; installed, it gives the app the store of its Pinia instance
const store = {
  ...createVuexStore(),
  install(app) {
    // eslint-disable-next-line no-param-reassign
    app.config.globalProperties.$store = vuexStoreOf(getActivePinia());
  },
};

// The store of the injected Pinia instance, else of the active one (undefined without any)
export const useStore = () => {
  const pinia = getActivePinia();
  return pinia && vuexStoreOf(pinia);
};

/**
 * Entries of a Vuex `map*` helper call: `(namespace, names)` or `(aliasToType)`
 * @param {string|Object} namespace
 * @param {Array|Object} [map]
 * @returns {Array<[string, string]>} [alias, type]
 */
const mapEntries = (namespace, map) => {
  const [prefix, names] = typeof namespace === 'string' ? [`${namespace}/`, map] : ['', namespace];
  const pairs = Array.isArray(names) ? names.map((name) => [name, name]) : Object.entries(names);
  return pairs.map(([alias, name]) => [alias, `${prefix}${name}`]);
};

// A Vuex `map*` helper, reading `this.$store` as Vuex did
const mapper = (call) => (namespace, map) => Object.fromEntries(mapEntries(namespace, map)
  .map(([alias, type]) => [alias, function mapped(payload) {
    return call(this.$store, type, payload);
  }]));

export const mapGetters = mapper((vuexStore, type) => vuexStore.getters[type]);
export const mapMutations = mapper((vuexStore, type, payload) => vuexStore.commit(type, payload));
export const mapActions = mapper((vuexStore, type, payload) => vuexStore.dispatch(type, payload));

export default store;
